import { useState } from 'react';
import { useUpdateLesson } from '@/api/course-builder';
import { FormShell } from '@/components/shared/form-shell';
import { FileUpload } from '@/components/shared/file-upload';
import type { BuilderLesson, LessonType, UpdateLessonInput } from '@/types';
import { fileNameFromUrl } from '@/lib/file-name';

type LessonFormDialogProps = { lesson: BuilderLesson; courseId: number; onClose: () => void };

const I =
  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring';
const L = 'mb-1.5 block text-sm font-medium text-foreground';

const LESSON_TYPES: { value: LessonType; label: string }[] = [
  { value: 'document_reading', label: 'Lectura de documento' },
  { value: 'video', label: 'Video' },
  { value: 'presentation', label: 'Presentación' },
  { value: 'content', label: 'Contenido' },
  { value: 'evidence_upload', label: 'Subir evidencia' },
  { value: 'practical_record', label: 'Registro práctico' },
  { value: 'practical_observation', label: 'Observación práctica' },
];

export function LessonFormDialog({ lesson, courseId, onClose }: LessonFormDialogProps) {
  const mutation = useUpdateLesson();
  const [title, setTitle] = useState(lesson.title);
  const [description, setDescription] = useState(lesson.description ?? '');
  const [content, setContent] = useState(lesson.content ?? '');
  const [lessonType, setLessonType] = useState<LessonType>(lesson.lesson_type);
  const [points, setPoints] = useState(lesson.points);
  const [sortOrder, setSortOrder] = useState(lesson.sort_order);
  const [file, setFile] = useState<{
    url: string;
    name: string;
    size: number;
    type: string;
  } | null>(
    lesson.file_url
      ? { url: lesson.file_url, name: fileNameFromUrl(lesson.file_url), size: 0, type: '' }
      : null,
  );
  const [videoUrl, setVideoUrl] = useState(lesson.video_url ?? '');
  const [requiresConfirmation, setRequiresConfirmation] = useState(lesson.requires_confirmation);
  const [isFree, setIsFree] = useState(lesson.is_free);

  const showFileUpload =
    lessonType === 'document_reading' ||
    lessonType === 'presentation' ||
    lessonType === 'evidence_upload';
  const showVideoUrl = lessonType === 'video';

  // Todos los tipos con archivo aceptan fotos (lectura y presentación desde
  // 2026-10-30); el visor de lecciones las muestra inline.
  const fileAccept = '.pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.webp';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    const data: UpdateLessonInput = {
      title: title.trim(),
      description: description.trim() || null,
      content: content.trim() || null,
      sort_order: sortOrder,
      points,
      file_url: showFileUpload && file ? file.url : null,
      video_url: showVideoUrl ? videoUrl.trim() || null : null,
      requires_confirmation: requiresConfirmation,
      is_free: isFree,
    };
    try {
      await mutation.mutateAsync({ lessonId: lesson.id, data, courseId });
      onClose();
    } catch {
      // Error handled by TanStack Query
    }
  };

  return (
    <FormShell title="Editar lección" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="lesson-title" className={L}>
            Título
          </label>
          <input
            id="lesson-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={I}
            placeholder="Título de la lección"
            maxLength={200}
            required
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="lesson-type" className={L}>
            Tipo de lección
          </label>
          <select
            id="lesson-type"
            value={lessonType}
            onChange={(e) => setLessonType(e.target.value as LessonType)}
            className={I}
          >
            {LESSON_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="lesson-desc" className={L}>
            Descripción
          </label>
          <textarea
            id="lesson-desc"
            value={description}
            rows={2}
            onChange={(e) => setDescription(e.target.value)}
            className={I}
            placeholder="Descripción o instrucciones..."
            maxLength={2000}
          />
        </div>

        <div>
          <label htmlFor="lesson-content" className={L}>
            Contenido
          </label>
          <textarea
            id="lesson-content"
            value={content}
            rows={4}
            onChange={(e) => setContent(e.target.value)}
            className={I}
            placeholder="Contenido de la lección (HTML permitido)..."
            maxLength={50000}
          />
        </div>

        {showVideoUrl && (
          <div>
            <label htmlFor="lesson-video" className={L}>
              URL del video
            </label>
            <input
              id="lesson-video"
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              className={I}
              placeholder="https://..."
            />
          </div>
        )}

        {showFileUpload && (
          <FileUpload
            folder="documents"
            accept={fileAccept}
            label="Archivo de la lección"
            value={file}
            onChange={setFile}
            disabled={mutation.isPending}
          />
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="lesson-points" className={L}>
              Puntos
            </label>
            <input
              id="lesson-points"
              type="number"
              min={0}
              max={1000}
              value={points}
              onChange={(e) => setPoints(Number(e.target.value))}
              className={I}
            />
          </div>
          <div>
            <label htmlFor="lesson-order" className={L}>
              Orden
            </label>
            <input
              id="lesson-order"
              type="number"
              min={0}
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
              className={I}
            />
          </div>
        </div>

        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={requiresConfirmation}
              onChange={(e) => setRequiresConfirmation(e.target.checked)}
              className="rounded border-input"
            />
            Requiere confirmacion
          </label>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={isFree}
              onChange={(e) => setIsFree(e.target.checked)}
              className="rounded border-input"
            />
            Lección libre
          </label>
        </div>

        {mutation.error && <p className="text-xs text-destructive">{mutation.error.message}</p>}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {mutation.isPending ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </FormShell>
  );
}
